import React, { useState, useEffect, useMemo } from "react";
import { 
  DataGrid, 
  GridColDef, 
  GridActionsCellItem,
  GridToolbar,
  GridFilterModel,
  GridSortModel,
  GridPaginationModel
} from "@mui/x-data-grid";
import { 
  CircularProgress, 
  TextField, 
  InputAdornment, 
  Typography, 
  Button, 
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import { fetchDepositRequestbyid } from "../../Common/SharedApis/DespositMethods";
import apiBaseUrl from "../../Common/ApiBaseUrl";
import GenericPopup from "../../components/Popup/GenericPopup";
import "./Deposite.css";

// Define the DepositRequest interface matching the API response
export interface DepositRequest {
  // API returns these exact property names
  depositId: number;
  amount: number;
  entryDateTime: string;
  statusId: number;
  statusName: string | null;
  isActive: boolean;
  remittanceCertificate: string;
  userName: string;
  userEmail: string;
  userPhoneNumber: string;
  coinName: string | null;
  coinLogo: string | null;
  chainName: string | null;
  depositFrom: string | null;
  depositTo: string | null;
  exchangeRate: number;
  fullName: string;
}

// Filter interface for server-side filtering
export interface DepositFilters {
  searchTerm?: string;
  statusId?: number;
  coinName?: string;
  isActive?: boolean;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
}

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken')?.toString();
};

// Fetch all deposit requests with optional filtering and pagination
const fetchAllDepositRequest = async (filters?: {
  searchTerm?: string;
  statusId?: number;
  coinName?: string;
  isActive?: boolean;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
  pageNumber?: number;
  pageSize?: number;
}) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    
    if (filters?.searchTerm) {
      queryParams.append('searchTerm', filters.searchTerm);
    }
    if (filters?.statusId !== undefined) {
      queryParams.append('statusId', filters.statusId.toString());
    }
    if (filters?.coinName) {
      queryParams.append('coinName', filters.coinName);
    }
    if (filters?.isActive !== undefined) {
      queryParams.append('isActive', filters.isActive.toString());
    }
    if (filters?.dateFrom) {
      queryParams.append('dateFrom', filters.dateFrom);
    }
    if (filters?.dateTo) {
      queryParams.append('dateTo', filters.dateTo);
    }
    if (filters?.minAmount !== undefined) {
      queryParams.append('minAmount', filters.minAmount.toString());
    }
    if (filters?.maxAmount !== undefined) {
      queryParams.append('maxAmount', filters.maxAmount.toString());
    }
    if (filters?.pageNumber !== undefined) {
      queryParams.append('pageNumber', filters.pageNumber.toString());
    }
    if (filters?.pageSize !== undefined) {
      queryParams.append('pageSize', filters.pageSize.toString());
    }

    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const url = filters ? 
      `${apiBaseUrl.apiBaseUrl}/Deposit/GetAllDepositRequests?${queryParams.toString()}` :
      `${apiBaseUrl.apiBaseUrl}/Deposit/GetAllDepositRequests`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch deposit requests: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('API Response:', data);
    
    // Return the full response for pagination
    return data;
  } catch (error) {
    console.error('Error fetching deposit requests:', error);
    throw error;
  }
};

const DepositRequest: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [depositRequests, setDepositRequests] = useState<DepositRequest[]>([]);
  const [filteredData, setFilteredData] = useState<DepositRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterValues: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  
  // Filter states
  const [filters, setFilters] = useState<DepositFilters>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [coinFilter, setCoinFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  
  // UI states
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DepositRequest | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<{ [key: number]: string }>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get unique values for filters
  const uniqueStatuses = useMemo(() => {
    const statuses = depositRequests
      .map(req => req.statusName)
      .filter((status): status is string => status != null && status !== '');
    return [...new Set(statuses)];
  }, [depositRequests]);

  const uniqueCoins = useMemo(() => {
    const coins = depositRequests
      .map(req => req.coinName)
      .filter((coin): coin is string => coin != null && coin !== '');
    return [...new Set(coins)];
  }, [depositRequests]);

  useEffect(() => {
    fetchData();
  }, []);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [depositRequests, filters]);

  const fetchData = async (pageNumber = 1, pageSize = 10) => {
    setLoading(true);
    try {
      console.log('Fetching deposit data...', { pageNumber, pageSize });
      const response = await fetchAllDepositRequest({
        pageNumber,
        pageSize,
        ...filters
      });
      console.log('Received data from API:', response);
      
      if (response && response.data && Array.isArray(response.data)) {
        console.log('Data is valid array with', response.data.length, 'items');
        // Sort the data by depositId in descending order (latest first)
        const sortedData = response.data
          .map((item: DepositRequest, index: number) => ({
            id: item.depositId, // Ensure unique ID for DataGrid
            ...item,
          }))
          .sort((a, b) => b.depositId - a.depositId);

        console.log('Sorted data:', sortedData);
        setDepositRequests(sortedData);
        setFilteredData(sortedData);
        setTotalCount(response.totalCount || 0);
      } else {
        console.warn('No valid data received:', response);
        setDepositRequests([]);
        setFilteredData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch deposit requests:", error);
      showSnackbar('Failed to fetch deposit requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...depositRequests];

    // Apply search filter
    if (filters.searchTerm) {
      filtered = filtered.filter(request =>
        request.userName.toLowerCase().includes(filters.searchTerm!.toLowerCase()) ||
        request.userEmail.toLowerCase().includes(filters.searchTerm!.toLowerCase()) ||
        request.fullName.toLowerCase().includes(filters.searchTerm!.toLowerCase()) ||
        request.depositId.toString().includes(filters.searchTerm!)
      );
    }

    // Apply status filter
    if (filters.statusId !== undefined) {
      filtered = filtered.filter(request => request.statusId === filters.statusId);
    }

    // Apply coin filter
    if (filters.coinName) {
      filtered = filtered.filter(request => request.coinName === filters.coinName);
    }

    // Apply active filter
    if (filters.isActive !== undefined) {
      filtered = filtered.filter(request => request.isActive === filters.isActive);
    }

    // Apply date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(request => 
        new Date(request.entryDateTime) >= new Date(filters.dateFrom!)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(request => 
        new Date(request.entryDateTime) <= new Date(filters.dateTo!)
      );
    }

    // Apply amount range filter
    if (filters.minAmount !== undefined) {
      filtered = filtered.filter(request => request.amount >= filters.minAmount!);
    }

    if (filters.maxAmount !== undefined) {
      filtered = filtered.filter(request => request.amount <= filters.maxAmount!);
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (newFilters: DepositFilters) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    // Fetch data with new filters
    fetchData(1, paginationModel.pageSize);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setStatusFilter('all');
    setCoinFilter('all');
    setActiveFilter('all');
    // Reset to first page and fetch data
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    fetchData(1, paginationModel.pageSize);
  };

  const handleViewClick = async (depositId: number) => {
    try {
      const data = await fetchDepositRequestbyid(depositId);
      if (data) {
        setSelectedRequest(data);
        setIsPopupOpen(true);
      }
    } catch (error) {
      console.error(`Error fetching deposit request by ID: ${depositId}`, error);
      showSnackbar('Failed to fetch deposit details', 'error');
    }
  };

  const handleApprove = () => {
    console.log("Approved");
    setIsPopupOpen(false);
    showSnackbar('Deposit request approved successfully', 'success');
  };

  const handleReject = () => {
    console.log("Rejected");
    setIsPopupOpen(false);
    showSnackbar('Deposit request rejected', 'error');
  };

  const handleWin = (depositId: number) => {
    console.log(`Deposit ${depositId} marked as Win`);
    setSelectedStatus(prev => ({ ...prev, [depositId]: "Win" }));
    showSnackbar('Deposit marked as Win', 'success');
  };

  const handleLose = (depositId: number) => {
    console.log(`Deposit ${depositId} marked as Lose`);
    setSelectedStatus(prev => ({ ...prev, [depositId]: "Lose" }));
    showSnackbar('Deposit marked as Lose', 'error');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle pagination changes
  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    // Fetch data for the new page
    fetchData(newPaginationModel.page + 1, newPaginationModel.pageSize);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (statusName: string | null) => {
    if (!statusName) return 'default';
    switch (statusName.toLowerCase()) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'DepositId',
      headerName: 'ID',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="600" color="primary">
          #{params.value}
        </Typography>
      ),
    },
    {
      field: 'fullName',
      headerName: 'User',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="600">
            {params.value}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            @{params.row.UserName}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'userEmail',
      headerName: 'Email',
      width: 220,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'coinName',
      headerName: 'Coin',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {params.row.coinLogo && (
            <Avatar 
              src={params.row.coinLogo} 
              alt={params.value || 'Coin'} 
              sx={{ width: 24, height: 24 }}
            />
          )}
          <Typography variant="body2" fontWeight="500">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="600" color="success.main">
          ${params.value.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'entryDateTime',
      headerName: 'Date',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'statusName',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Unknown'}
          color={getStatusColor(params.value) as any}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      field: 'exchangeRate',
      headerName: 'Exchange Rate',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 400,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title="View Details">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleViewClick(params.row.depositId)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Mark as Win">
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={selectedStatus[params.row.depositId] === "Win" ? <CheckIcon /> : <ThumbUpIcon />}
              onClick={() => handleWin(params.row.depositId)}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              Win
            </Button>
          </Tooltip>
          
          <Tooltip title="Mark as Lose">
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={selectedStatus[params.row.depositId] === "Lose" ? <CheckIcon /> : <ThumbDownIcon />}
              onClick={() => handleLose(params.row.depositId)}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              Lose
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress size={60} />
        <Typography variant="h6" ml={2}>
          Loading deposit requests...
        </Typography>
      </Box>
    );
  }

  const renderPopupContent = (data: DepositRequest | null) => (
    data ? (
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Deposit Details
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">ID</Typography>
            <Typography variant="body1" gutterBottom>#{data.depositId}</Typography>
            
            <Typography variant="subtitle2" color="textSecondary">User Name</Typography>
            <Typography variant="body1" gutterBottom>{data.fullName}</Typography>
            
            <Typography variant="subtitle2" color="textSecondary">Email</Typography>
            <Typography variant="body1" gutterBottom>{data.userEmail}</Typography>
            
            <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
            <Typography variant="body1" gutterBottom>{data.userPhoneNumber || 'N/A'}</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
            <Typography variant="h6" color="success.main" gutterBottom>${data.amount.toLocaleString()}</Typography>
            
            <Typography variant="subtitle2" color="textSecondary">Coin</Typography>
            <Typography variant="body1" gutterBottom>{data.coinName || 'N/A'}</Typography>
            
            <Typography variant="subtitle2" color="textSecondary">Chain</Typography>
            <Typography variant="body1" gutterBottom>{data.chainName || 'N/A'}</Typography>
            
            <Typography variant="subtitle2" color="textSecondary">Exchange Rate</Typography>
            <Typography variant="body1" gutterBottom>{data.exchangeRate || 'N/A'}</Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">Date</Typography>
            <Typography variant="body1" gutterBottom>{formatDate(data.entryDateTime)}</Typography>
            
            <Typography variant="subtitle2" color="textSecondary">Status</Typography>
            <Chip
              label={data.statusName || 'Unknown'}
              color={getStatusColor(data.statusName) as any}
              sx={{ mb: 1 }}
            />
            
            {data.remittanceCertificate && (
              <>
                <Typography variant="subtitle2" color="textSecondary" display="block">
                  Remittance Certificate
                </Typography>
                <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                  {data.remittanceCertificate}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
      </Box>
    ) : (
      <Typography variant="body1">No data available</Typography>
    )
  );

  return (
    <Box className="deposit-container">
      {/* Header Section */}
      <Card className="deposit-header" elevation={0}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h4" component="h1" className="page-title">
                Deposit Requests
              </Typography>
              <Typography variant="body1" color="textSecondary" className="page-subtitle">
                Manage and review user deposit requests
              </Typography>
            </Box>
            
            <Box display="flex" gap={2}>
                             <Button
                 variant="outlined"
                 startIcon={<RefreshIcon />}
                 onClick={() => fetchData(paginationModel.page + 1, paginationModel.pageSize)}
                 disabled={loading}
               >
                 Refresh
               </Button>
            </Box>
          </Box>

          {/* Search and Filters */}
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search by User, Email, or ID"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleFilterChange({ ...filters, searchTerm: e.target.value });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    const statusId = e.target.value === 'all' ? undefined : parseInt(e.target.value);
                    handleFilterChange({ ...filters, statusId });
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {uniqueStatuses.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Coin</InputLabel>
                <Select
                  value={coinFilter}
                  onChange={(e) => {
                    setCoinFilter(e.target.value);
                    const coinName = e.target.value === 'all' ? undefined : e.target.value;
                    handleFilterChange({ ...filters, coinName });
                  }}
                  label="Coin"
                >
                  <MenuItem value="all">All Coins</MenuItem>
                  {uniqueCoins.map((coin) => (
                    <MenuItem key={coin} value={coin}>{coin}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Active</InputLabel>
                <Select
                  value={activeFilter}
                  onChange={(e) => {
                    setActiveFilter(e.target.value);
                    const isActive = e.target.value === 'all' ? undefined : e.target.value === 'active';
                    handleFilterChange({ ...filters, isActive });
                  }}
                  label="Active"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleClearFilters}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card className="deposit-grid-card" elevation={0}>
                 <DataGrid
           rows={filteredData}
           columns={columns}
           pagination
           paginationModel={paginationModel}
           onPaginationModelChange={handlePaginationModelChange}
           pageSizeOptions={[10, 25, 50, 100]}
           rowCount={totalCount}
           paginationMode="server"
           filterModel={filterModel}
           onFilterModelChange={setFilterModel}
           sortModel={sortModel}
           onSortModelChange={setSortModel}
           disableRowSelectionOnClick
           autoHeight
           slots={{
             toolbar: GridToolbar,
           }}
           slotProps={{
             toolbar: {
               showQuickFilter: true,
               quickFilterProps: { debounceMs: 500 },
             },
           }}
           sx={{
             border: 'none',
             '& .MuiDataGrid-cell': {
               borderBottom: '1px solid #f0f0f0',
             },
             '& .MuiDataGrid-columnHeaders': {
               backgroundColor: '#f8f9fa',
               borderBottom: '2px solid #e0e0e0',
             },
           }}
         />
      </Card>

      {/* Summary */}
      <Card className="deposit-summary" elevation={0}>
        <CardContent>
                     <Box display="flex" justifyContent="space-between" alignItems="center">
             <Typography variant="body1" color="textSecondary">
               Showing {filteredData.length} of {totalCount} deposit requests (Page {paginationModel.page + 1} of {Math.ceil(totalCount / paginationModel.pageSize)})
             </Typography>
             <Typography variant="body2" color="textSecondary">
               Last updated: {new Date().toLocaleTimeString()}
             </Typography>
           </Box>
        </CardContent>
      </Card>

      {/* Popup */}
      <GenericPopup
        open={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onApprove={handleApprove}
        onReject={handleReject}
        data={selectedRequest}
        renderContent={renderPopupContent}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DepositRequest;
